import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatError } from "../api";
import {
  ClipboardList, CheckCircle2, Loader2, Calendar, MapPin, User,
  ArrowRight, ListChecks, Briefcase,
} from "lucide-react";

const STATUS_META = {
  pending:            { label: "Pending",            color: "#0099FF" },
  in_progress:        { label: "In progress",        color: "#0040A8" },
  ready_to_complete:  { label: "Ready to complete",  color: "#16A34A" },
  completed:          { label: "Completed",          color: "#0F7C39" },
};

function StatCard({ k, n, color, active, onClick }) {
  const meta = STATUS_META[k] || { label: k, color };
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={`onboarding-stat-${k}`}
      className="text-left card card-pad transition-shadow hover:shadow-md"
      style={{ borderColor: active ? meta.color : "var(--rule)", borderWidth: active ? 2 : 1 }}
    >
      <div className="eyebrow" style={{ color: meta.color }}>{meta.label}</div>
      <div className="font-display font-extrabold text-3xl mt-1" style={{ color: meta.color }}>{n}</div>
    </button>
  );
}

export default function CompanyOnboarding() {
  const [rows, setRows] = useState([]);
  const [stats, setStats] = useState(null);
  const [filter, setFilter] = useState(""); // empty = all
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  const load = useCallback(async () => {
    setLoading(true); setErr("");
    try {
      const params = filter ? { status: filter } : {};
      const [list, st] = await Promise.all([
        api.get("/company/onboarding", { params }),
        api.get("/onboarding/stats"),
      ]);
      setRows(list.data || []);
      setStats(st.data);
    } catch (e) {
      setErr(formatError(e));
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="space-y-5" data-testid="company-onboarding-page">
      <div className="rounded-sm p-6 text-white bg-brand-gradient flex items-start justify-between gap-3 flex-wrap">
        <div>
          <span className="eyebrow on-grad-muted inline-flex items-center gap-1.5"><ClipboardList size={11} /> Onboarding</span>
          <h1 className="font-display font-extrabold text-2xl md:text-3xl mt-1">New hires &amp; joining</h1>
          <p className="text-sm text-white/80 mt-1 max-w-xl">
            Track every accepted offer from acceptance through day one. Set joining details, manage the checklist, and welcome the team.
          </p>
        </div>
        <Link to="/company/pipeline" className="btn-on-gradient inline-flex items-center gap-1.5" data-testid="onboarding-to-pipeline">
          <Briefcase size={14} /> Back to pipeline
        </Link>
      </div>

      {err && <div className="text-sm text-red-600 bg-red-50 rounded-xl px-3 py-2">{err}</div>}

      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {Object.keys(STATUS_META).map((k) => (
            <StatCard
              key={k}
              k={k}
              n={stats[k] || 0}
              color={STATUS_META[k].color}
              active={filter === k}
              onClick={() => setFilter(filter === k ? "" : k)}
            />
          ))}
        </div>
      )}

      <div className="card card-pad">
        <div className="flex items-center justify-between mb-3">
          <h2 className="font-display font-extrabold text-lg">
            {filter ? STATUS_META[filter]?.label || filter : "All onboardings"}
            <span className="text-muted text-sm font-normal ml-2">({rows.length})</span>
          </h2>
          {filter && (
            <button type="button" onClick={() => setFilter("")} className="text-xs font-semibold text-signal" data-testid="onboarding-clear-filter">
              Clear filter
            </button>
          )}
        </div>

        {loading ? (
          <div className="flex items-center gap-2 text-sm text-muted py-6"><Loader2 size={14} className="animate-spin" /> Loading…</div>
        ) : rows.length === 0 ? (
          <div className="border-2 border-dashed rounded-xl p-8 text-center" style={{ borderColor: "var(--rule)" }} data-testid="onboarding-empty">
            <ListChecks size={26} className="mx-auto text-muted mb-2" />
            <div className="font-semibold text-sm">No onboardings yet</div>
            <p className="text-xs text-muted mt-1 max-w-md mx-auto">
              When a candidate accepts an offer in your pipeline, an onboarding record is created here automatically.
            </p>
          </div>
        ) : (
          <ul className="divide-y" style={{ borderColor: "var(--rule)" }}>
            {rows.map((r) => {
              const meta = STATUS_META[r.status] || { label: r.status, color: "#94A3B8" };
              const tasks = r.tasks || [];
              const done = tasks.filter((t) => t.done).length;
              return (
                <li key={r.id} data-testid={`onboarding-row-${r.id}`} className="py-3">
                  <Link to={`/company/onboarding/${r.id}`} className="flex items-start gap-3 group">
                    <div className="w-10 h-10 rounded-full grid place-items-center text-white text-xs font-bold shrink-0"
                      style={{ background: "linear-gradient(135deg, #006CFF 0%, #9933FF 100%)" }}>
                      {(r.student_name || "?").split(/\s+/).slice(0, 2).map(w => w[0]).join("").toUpperCase()}
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-semibold text-sm">{r.student_name || "Unnamed"}</span>
                        <span className="text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded text-white" style={{ background: meta.color }}>
                          {meta.label}
                        </span>
                      </div>
                      <div className="text-xs text-muted mt-0.5">{r.posting_title}</div>
                      <div className="text-[11px] text-muted mt-1 flex flex-wrap gap-x-3 gap-y-1">
                        {r.joining_date && <span className="inline-flex items-center gap-1"><Calendar size={11} />{r.joining_date}</span>}
                        {r.work_location && <span className="inline-flex items-center gap-1"><MapPin size={11} />{r.work_location}</span>}
                        {r.reporting_manager && <span className="inline-flex items-center gap-1"><User size={11} />{r.reporting_manager}</span>}
                        <span className="inline-flex items-center gap-1"><CheckCircle2 size={11} />{done}/{tasks.length} tasks</span>
                      </div>
                    </div>
                    <ArrowRight size={16} className="text-muted opacity-0 group-hover:opacity-100 transition-opacity mt-2" />
                  </Link>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
}
